import { useState, useMemo, useCallback, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Typography,
  Divider,
} from "@mui/material";
import {
  Home,
  Banknote,
  ArrowLeftRight,
  BatteryCharging,
  Calculator,
  UserPlus,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  X,
} from "lucide-react";
import navConfig from "./navConfig";
import { useAuth } from "../../contexts/AuthContext";

const BRAND = {
  primary: "#1DA7A7",
  primaryDark: "#0f766e",
  tint: "#E0F7F5",
  border: "#E6EEEC",
  textMuted: "#6B7A78",
};

const EXPANDED_WIDTH = 248;
const COLLAPSED_WIDTH = 72;
const STORAGE_KEY = "sidebar:collapsed";

const Sidebar = ({
  userRole,
  title = "Menu",
  mobileOpen = false,
  onMobileClose,
}) => {
  const location = useLocation();

  const { hasAccess } = useAuth();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const checkAccess = useCallback(
    (role) => {
      if (!role) return true;

      if (hasAccess) {
        return hasAccess(role);
      }

      return role === userRole;
    },
    [hasAccess, userRole],
  );

  const visibleNav = useMemo(
    () =>
      navConfig
        .map((entry) => {
          if (entry.type === "link") {
            return checkAccess(entry.role) ? entry : null;
          }

          const visibleChildren = entry.children.filter((child) =>
            checkAccess(child.role),
          );

          if (visibleChildren.length === 0) {
            return null;
          }

          return {
            ...entry,
            children: visibleChildren,
          };
        })
        .filter(Boolean),
    [checkAccess],
  );

  // Returns -1 if no match, otherwise the number of query params that matched.
  // More params matched = more specific.
  const getMatchScore = useCallback(
    (targetPath) => {
      if (!targetPath) return -1;

      const [targetPathname, targetSearch = ""] = targetPath.split("?");
      if (targetPathname !== location.pathname) return -1;

      const targetParams = new URLSearchParams(targetSearch);
      const currentParams = new URLSearchParams(location.search);

      let score = 0;
      for (const [key, value] of targetParams.entries()) {
        if (currentParams.get(key) !== value) return -1;
        score++;
      }
      return score;
    },
    [location.pathname, location.search],
  );

  const isPathActive = useCallback(
    (targetPath) => getMatchScore(targetPath) >= 0,
    [getMatchScore],
  );

  const getActiveChildId = useCallback(
    (group) => {
      let bestId = null;
      let bestScore = -1;

      group.children.forEach((child) => {
        const score = getMatchScore(child.path);
        if (score > bestScore) {
          bestScore = score;
          bestId = child.id;
        }
      });

      return bestId;
    },
    [getMatchScore],
  );

  const isGroupActive = useCallback(
    (group) => getActiveChildId(group) !== null,
    [getActiveChildId],
  );

  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    visibleNav.forEach((entry) => {
      if (entry.type === "group") initial[entry.id] = isGroupActive(entry);
    });
    return initial;
  });

  useEffect(() => {
    visibleNav.forEach((entry) => {
      if (entry.type === "group" && isGroupActive(entry)) {
        setOpenGroups((prev) =>
          prev[entry.id] ? prev : { ...prev, [entry.id]: true },
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const toggleGroup = (id) =>
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const isRailCollapsed = collapsed; // only meaningful on desktop

  const renderNavList = (showLabels) => (
    <List sx={{ px: 1, py: 1 }}>
      {visibleNav.map((entry) => {
        const IconComp = entry.icon || Home;

        if (entry.type === "link") {
          const active = isPathActive(entry.path);
          return (
            <ListItemButton
              key={entry.id}
              component={Link}
              to={entry.path}
              onClick={onMobileClose}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                minHeight: 40,
                color: active ? BRAND.primary : "text.primary",
                bgcolor: active ? BRAND.tint : "transparent",
                fontWeight: active ? 600 : 400,
                position: "relative",
                "&:hover": { bgcolor: active ? BRAND.tint : "action.hover" },
                "&::before": active
                  ? {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 6,
                      bottom: 6,
                      width: 3,
                      borderRadius: "0 3px 3px 0",
                      bgcolor: BRAND.primary,
                    }
                  : undefined,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: showLabels ? 1.5 : 0,
                  justifyContent: "center",
                }}
              >
                <IconComp
                  size={18}
                  color={active ? BRAND.primary : BRAND.textMuted}
                />
              </ListItemIcon>
              {showLabels && (
                <ListItemText
                  primary={entry.label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: active ? 600 : 500,
                  }}
                />
              )}
            </ListItemButton>
          );
        }

        const isOpen = !!openGroups[entry.id];
        const groupActive = isGroupActive(entry);
        const activeChildId = getActiveChildId(entry);

        return (
          <Box key={entry.id} sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() =>
                showLabels ? toggleGroup(entry.id) : setCollapsed(false)
              }
              sx={{
                borderRadius: 1.5,
                minHeight: 40,
                color: groupActive ? BRAND.primary : "text.primary",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: showLabels ? 1.5 : 0,
                  justifyContent: "center",
                }}
              >
                <IconComp
                  size={19}
                  color={groupActive ? BRAND.primary : BRAND.textMuted}
                />
              </ListItemIcon>
              {showLabels && (
                <>
                  <ListItemText
                    primary={entry.label}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: groupActive ? 600 : 500,
                    }}
                  />
                  <ChevronRight
                    size={16}
                    color={BRAND.textMuted}
                    style={{
                      transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                      transition: "transform 0.15s ease",
                    }}
                  />
                </>
              )}
            </ListItemButton>

            {showLabels && (
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List disablePadding sx={{ pl: 4.5, pr: 1 }}>
                  {entry.children.map((child) => {
                    const active = child.id === activeChildId;
                    return (
                      <ListItemButton
                        key={child.id}
                        component={Link}
                        to={child.path}
                        onClick={onMobileClose}
                        sx={{
                          borderRadius: 1.5,
                          minHeight: 34,
                          mb: 0.25,
                          color: active ? BRAND.primary : "text.secondary",
                          bgcolor: active ? BRAND.tint : "transparent",
                          "&:hover": {
                            bgcolor: active ? BRAND.tint : "action.hover",
                          },
                        }}
                      >
                        <ListItemText
                          primary={child.label}
                          primaryTypographyProps={{
                            fontSize: 13.5,
                            fontWeight: active ? 600 : 400,
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            )}
          </Box>
        );
      })}
    </List>
  );

  const desktopHeader = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: 52,
        px: isRailCollapsed ? 1 : 2,
        borderBottom: `1px solid ${BRAND.border}`,
      }}
    >
      {!isRailCollapsed && (
        <Typography
          sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}
        >
          {title}
        </Typography>
      )}
      <IconButton
        size="small"
        onClick={toggleCollapsed}
        aria-label={isRailCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        sx={{
          color: BRAND.textMuted,
          ml: isRailCollapsed ? "auto" : 0,
          mr: isRailCollapsed ? "auto" : 0,
        }}
      >
        {isRailCollapsed ? (
          <PanelLeftOpen size={18} />
        ) : (
          <PanelLeftClose size={18} />
        )}
      </IconButton>
    </Box>
  );

  const mobileHeader = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: 52,
        px: 2,
        borderBottom: `1px solid ${BRAND.border}`,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}>
        {title}
      </Typography>
      <IconButton
        size="small"
        onClick={onMobileClose}
        aria-label="Close menu"
        sx={{ color: BRAND.textMuted }}
      >
        <X size={18} />
      </IconButton>
    </Box>
  );

  return (
    <>
      {/* Desktop: docked, in-flow rail. Hidden below md - the temporary Drawer takes over. */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          width: isRailCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
          height: "100%",
          bgcolor: "#FFFFFF",
          borderRight: `1px solid ${BRAND.border}`,
          transition: "width 0.18s ease",
          overflow: "hidden",
        }}
      >
        {desktopHeader}
        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          {renderNavList(!isRailCollapsed)}
        </Box>
      </Box>

      {/* Mobile: overlay drawer, toggled from the Header's menu button */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: EXPANDED_WIDTH,
            boxSizing: "border-box",
          },
        }}
      >
        {mobileHeader}
        <Divider />
        {renderNavList(true)}
      </Drawer>
    </>
  );
};

export default Sidebar;
